import {isAfter, isBefore, min, parseISO} from 'date-fns';
import {match, Pattern} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {BUSINESS_CASE_SIGNAL_CODES} from '../constants/BUSINESS_CASE_SIGNAL_CODES';
import type {BusinessCaseSignalEvidenceOmission} from '../constants/BUSINESS_CASE_SIGNAL_EVIDENCE_OMISSIONS';
import {differenceInWholeDays} from '../interest/differenceInWholeDays';
import {differenceInWholeHours} from '../interest/differenceInWholeHours';
import type {BusinessCaseGeneralSignalEvidence} from '../types/BusinessCaseGeneralSignalEvidence';
import type {BusinessCasePaymentEvidence} from '../types/BusinessCasePaymentEvidence';
import type {BusinessCasePaymentKind} from '../types/BusinessCasePaymentKind';
import type {BusinessCasePlannedActivity} from '../types/BusinessCasePlannedActivity';
import type {BusinessCaseSignal} from '../types/BusinessCaseSignal';
import type {BusinessCaseSignalCode} from '../types/BusinessCaseSignalCode';
import type {BusinessCaseSignalSeverity} from '../types/BusinessCaseSignalSeverity';
import type {BusinessCaseTimelineEventKind} from '../types/BusinessCaseTimelineEventKind';
import type {BusinessCaseUnpaidSignalEvidence} from '../types/BusinessCaseUnpaidSignalEvidence';
import type {DetectBusinessCaseSignalsInput} from '../types/DetectBusinessCaseSignalsInput';

const HOURS_PER_DAY = 24;

const CUSTOMER_REPLY_EVENT_KINDS: readonly BusinessCaseTimelineEventKind[] = [
  'message',
  'call',
  'email',
  'sms',
];

const BUYING_SIGNAL_CODES: readonly BusinessCaseSignalCode[] = [
  'expectedPurchaseDatePassed',
  'expectedPriceAboveMax',
];

const BUSINESS_CASE_GOING_COLD_SUPPRESSING_SIGNAL_CODES: readonly BusinessCaseSignalCode[] = [
  'customerAwaitingReply',
  'offerSentNoResponse',
  'offerOpenedNoReply',
];

type PlannedActivityWithDueAt = BusinessCasePlannedActivity & {dueAt: string};

type GeneralSignalCode = Exclude<BusinessCaseSignalCode, 'depositUnpaid' | 'invoiceUnpaid'>;
type UnpaidSignalCode = Extract<BusinessCaseSignalCode, 'depositUnpaid' | 'invoiceUnpaid'>;

const createBusinessCaseSignal = (
  code: GeneralSignalCode,
  severity: BusinessCaseSignalSeverity,
  evidence: BusinessCaseGeneralSignalEvidence,
  evaluatedAt: string,
  evidenceOmissions: BusinessCaseSignalEvidenceOmission[] = []
): BusinessCaseSignal => ({
  code,
  severity,
  evidence,
  evaluatedAt,
  evidenceIncomplete: evidenceOmissions.length > 0,
  evidenceOmissions,
});

const createUnpaidBusinessCaseSignal = (
  code: UnpaidSignalCode,
  evidence: BusinessCaseUnpaidSignalEvidence,
  evaluatedAt: string
): BusinessCaseSignal => ({
  code,
  severity: 'danger',
  evidence,
  evaluatedAt,
  evidenceIncomplete: false,
  evidenceOmissions: [],
});

const getTimelineEvidenceOmissions = (
  input: DetectBusinessCaseSignalsInput
): BusinessCaseSignalEvidenceOmission[] =>
  input.facts.completeness.timelineComplete ? [] : ['timelineTruncated'];

const getPlannedActivityEvidenceOmissions = (
  input: DetectBusinessCaseSignalsInput
): BusinessCaseSignalEvidenceOmission[] => [
  ...((input.facts.completeness.omittedPlannedActivityCount ?? 0) > 0
    ? ['plannedActivitiesTruncated' as const]
    : []),
  ...(input.facts.completeness.plannedActivitiesComplete ? [] : ['otherUsersTasks' as const]),
];

const isAtOrAfter = (candidateAt: string, boundaryAt: string) =>
  !isBefore(parseISO(candidateAt), parseISO(boundaryAt));

const isSignalApplicable = (code: BusinessCaseSignalCode, input: DetectBusinessCaseSignalsInput) =>
  match(input.facts.type)
    .with('BUYING', () => BUYING_SIGNAL_CODES.includes(code))
    .with('SELLING', () => !BUYING_SIGNAL_CODES.includes(code))
    .exhaustive();

const detectCustomerAwaitingReply = (
  input: DetectBusinessCaseSignalsInput
): BusinessCaseSignal | null => {
  const inboundEvent = input.facts.timeline.find(
    (event) => event.direction === 'inbound' && CUSTOMER_REPLY_EVENT_KINDS.includes(event.kind)
  );

  return match(inboundEvent)
    .returnType<BusinessCaseSignal | null>()
    .with(Pattern.nullish, () => null)
    .otherwise((event) => {
      const hasReply = input.facts.timeline.some(
        (candidate) =>
          candidate.direction === 'outbound' && isAtOrAfter(candidate.occurredAt, event.occurredAt)
      );
      const waitingSinceHours = differenceInWholeHours(input.evaluatedAt, event.occurredAt);

      return match({
        hasReply,
        reachedMinimum: waitingSinceHours >= input.policy.awaitingReplyMinimumHours,
      })
        .returnType<BusinessCaseSignal | null>()
        .with({hasReply: false, reachedMinimum: true}, () =>
          createBusinessCaseSignal(
            'customerAwaitingReply',
            differenceInWholeDays(input.evaluatedAt, event.occurredAt) >
              input.policy.coldThresholdDays
              ? 'danger'
              : 'warning',
            {
              waitingSinceAt: event.occurredAt,
              waitingSinceHours,
              triggeringEventId: event.id,
            },
            input.evaluatedAt,
            getTimelineEvidenceOmissions(input)
          )
        )
        .otherwise(() => null);
    });
};

const isOverdueTask = (
  activity: BusinessCasePlannedActivity,
  evaluatedAt: string
): activity is PlannedActivityWithDueAt =>
  activity.kind === 'task' &&
  !activity.completed &&
  isNotNil(activity.dueAt) &&
  isBefore(parseISO(activity.dueAt), parseISO(evaluatedAt));

const isUpcomingTestDrive = (
  activity: BusinessCasePlannedActivity,
  evaluatedAt: string
): activity is PlannedActivityWithDueAt =>
  activity.kind === 'testDrive' &&
  !activity.completed &&
  isNotNil(activity.dueAt) &&
  isAtOrAfter(activity.dueAt, evaluatedAt);

const detectOverdueTask = (input: DetectBusinessCaseSignalsInput): BusinessCaseSignal | null => {
  const overdueTasks = input.facts.plannedActivities.filter((activity) =>
    isOverdueTask(activity, input.evaluatedAt)
  );

  return match(overdueTasks.length)
    .returnType<BusinessCaseSignal | null>()
    .when(
      (count) => count > 0,
      (count) => {
        const oldestDueAt = min(overdueTasks.map((activity) => parseISO(activity.dueAt)));

        return createBusinessCaseSignal(
          'overdueTask',
          'warning',
          {
            overdueTaskCount: count,
            overdueBySinceDays: differenceInWholeDays(input.evaluatedAt, oldestDueAt.toISOString()),
            oldestOverdueAt: oldestDueAt.toISOString(),
          },
          input.evaluatedAt,
          getPlannedActivityEvidenceOmissions(input)
        );
      }
    )
    .otherwise(() => null);
};

const detectMissingNextStep = (
  input: DetectBusinessCaseSignalsInput,
  emittedSignalCodes: ReadonlySet<BusinessCaseSignalCode>
): BusinessCaseSignal | null => {
  const hasCurrentOrFutureActivity = input.facts.plannedActivities.some(
    (activity) =>
      !activity.completed &&
      (isNil(activity.dueAt) || isAtOrAfter(activity.dueAt, input.evaluatedAt))
  );

  return match({hasCurrentOrFutureActivity, hasOverdueTask: emittedSignalCodes.has('overdueTask')})
    .returnType<BusinessCaseSignal | null>()
    .with({hasCurrentOrFutureActivity: false, hasOverdueTask: false}, () =>
      createBusinessCaseSignal(
        'missingNextStep',
        'info',
        {},
        input.evaluatedAt,
        getPlannedActivityEvidenceOmissions(input)
      )
    )
    .otherwise(() => null);
};

const detectGoingCold = (
  input: DetectBusinessCaseSignalsInput,
  emittedSignalCodes: ReadonlySet<BusinessCaseSignalCode>
): BusinessCaseSignal | null => {
  const lastActivityAt = input.facts.timeline[0]?.occurredAt ?? input.facts.updatedAt;
  const inactiveForDays = differenceInWholeDays(input.evaluatedAt, lastActivityAt);
  const isSuppressed = BUSINESS_CASE_GOING_COLD_SUPPRESSING_SIGNAL_CODES.some((code) =>
    emittedSignalCodes.has(code)
  );

  return match({isSuppressed, exceededThreshold: inactiveForDays > input.policy.coldThresholdDays})
    .returnType<BusinessCaseSignal | null>()
    .with({isSuppressed: false, exceededThreshold: true}, () =>
      createBusinessCaseSignal(
        'goingCold',
        'warning',
        {lastActivityAt, inactiveForDays},
        input.evaluatedAt,
        getTimelineEvidenceOmissions(input)
      )
    )
    .otherwise(() => null);
};

const detectEngagement = (input: DetectBusinessCaseSignalsInput): BusinessCaseSignal | null => {
  const offerOpenedEvent = input.facts.timeline.find((event) => event.kind === 'offerOpened');
  const offerOpenedAt = input.facts.onlineOffer?.lastOpenedAt ?? offerOpenedEvent?.occurredAt;
  const futureTestDrive = input.facts.plannedActivities.find(
    (activity) =>
      activity.kind === 'testDrive' &&
      !activity.completed &&
      isNotNil(activity.dueAt) &&
      isAfter(parseISO(activity.dueAt), parseISO(input.evaluatedAt))
  );

  return match({offerOpenedAt, futureTestDrive})
    .returnType<BusinessCaseSignal | null>()
    .with({offerOpenedAt: Pattern.string}, ({offerOpenedAt: openedAt}) =>
      createBusinessCaseSignal(
        'engagement',
        'info',
        {
          engagementKind: 'offerOpened',
          offerOpenedAt: openedAt,
          ...(isNil(offerOpenedEvent) ? {} : {triggeringEventId: offerOpenedEvent.id}),
        },
        input.evaluatedAt
      )
    )
    .with({futureTestDrive: Pattern.nonNullable}, ({futureTestDrive: activity}) =>
      createBusinessCaseSignal(
        'engagement',
        'info',
        {engagementKind: 'testDrivePlanned', triggeringEventId: activity.id},
        input.evaluatedAt
      )
    )
    .otherwise(() => null);
};

const getOfferSentAt = (input: DetectBusinessCaseSignalsInput) =>
  input.facts.onlineOffer?.firstSentAt ??
  input.facts.onlineOfferFirstSentAt ??
  input.facts.timeline.find((event) => event.kind === 'offerSent')?.occurredAt ??
  null;

const getOfferValidTo = (input: DetectBusinessCaseSignalsInput) =>
  input.facts.onlineOffer?.validTo ?? input.facts.offerValidTo;

const getOfferOpenedAt = (input: DetectBusinessCaseSignalsInput) =>
  input.facts.onlineOffer?.lastOpenedAt ??
  input.facts.timeline.find((event) => event.kind === 'offerOpened')?.occurredAt ??
  null;

const detectOfferNotSent = (input: DetectBusinessCaseSignalsInput): BusinessCaseSignal | null => {
  const hasKnownOfferEvidence = input.facts.completeness.offerEvidenceComplete;
  const offerSentAt = getOfferSentAt(input);
  const ageDays = differenceInWholeDays(input.evaluatedAt, input.facts.createdAt);

  return match({
    activeOffer: input.facts.state === 'OFFER',
    hasKnownOfferEvidence,
    offerSent: isNotNil(offerSentAt),
    reachedMinimum: ageDays >= input.policy.offerNotSentMinimumDays,
  })
    .returnType<BusinessCaseSignal | null>()
    .with(
      {
        activeOffer: true,
        hasKnownOfferEvidence: true,
        offerSent: false,
        reachedMinimum: true,
      },
      () => createBusinessCaseSignal('offerNotSent', 'warning', {}, input.evaluatedAt)
    )
    .otherwise(() => null);
};

const detectOfferSentNoResponse = (
  input: DetectBusinessCaseSignalsInput
): BusinessCaseSignal | null => {
  const offerSentAt = getOfferSentAt(input);

  return match(offerSentAt)
    .returnType<BusinessCaseSignal | null>()
    .with(Pattern.nullish, () => null)
    .otherwise((sentAt) => {
      const hasResponse = input.facts.timeline.some(
        (event) => event.direction === 'inbound' && isAtOrAfter(event.occurredAt, sentAt)
      );
      const offerOpenedAt = getOfferOpenedAt(input);
      const hasOpened =
        (input.facts.onlineOffer?.openCount ?? 0) > 0 ||
        (isNotNil(offerOpenedAt) && isAtOrAfter(offerOpenedAt, sentAt));
      const waitingDays = differenceInWholeDays(input.evaluatedAt, sentAt);

      return match({
        activeOffer: input.facts.state === 'OFFER',
        hasResponse,
        hasOpened,
        reachedMinimum: waitingDays >= input.policy.offerSentNoResponseMinimumDays,
      })
        .returnType<BusinessCaseSignal | null>()
        .with({activeOffer: true, hasResponse: false, hasOpened: false, reachedMinimum: true}, () =>
          createBusinessCaseSignal(
            'offerSentNoResponse',
            'warning',
            {offerSentAt: sentAt, waitingSinceAt: sentAt},
            input.evaluatedAt,
            getTimelineEvidenceOmissions(input)
          )
        )
        .otherwise(() => null);
    });
};

const detectOfferOpenedNoReply = (
  input: DetectBusinessCaseSignalsInput
): BusinessCaseSignal | null => {
  const offerOpenedEvent = input.facts.timeline.find((event) => event.kind === 'offerOpened');
  const offerOpenedAt = getOfferOpenedAt(input);

  return match(offerOpenedAt)
    .returnType<BusinessCaseSignal | null>()
    .with(Pattern.nullish, () => null)
    .otherwise((openedAt) => {
      const hasReply = input.facts.timeline.some(
        (event) =>
          event.direction === 'inbound' && isAfter(parseISO(event.occurredAt), parseISO(openedAt))
      );

      return match({activeOffer: input.facts.state === 'OFFER', hasReply})
        .returnType<BusinessCaseSignal | null>()
        .with({activeOffer: true, hasReply: false}, () =>
          createBusinessCaseSignal(
            'offerOpenedNoReply',
            'warning',
            {
              offerOpenedAt: openedAt,
              ...(isNil(offerOpenedEvent) ? {} : {triggeringEventId: offerOpenedEvent.id}),
            },
            input.evaluatedAt,
            getTimelineEvidenceOmissions(input)
          )
        )
        .otherwise(() => null);
    });
};

const detectOfferExpiringSoon = (
  input: DetectBusinessCaseSignalsInput
): BusinessCaseSignal | null =>
  match(getOfferValidTo(input))
    .returnType<BusinessCaseSignal | null>()
    .with(Pattern.nullish, () => null)
    .otherwise((offerValidTo) => {
      const daysUntilExpiry = differenceInWholeDays(offerValidTo, input.evaluatedAt);

      return match({
        activeOffer: input.facts.state === 'OFFER',
        inWindow: daysUntilExpiry >= 0 && daysUntilExpiry <= input.policy.offerExpiringDays,
      })
        .returnType<BusinessCaseSignal | null>()
        .with({activeOffer: true, inWindow: true}, () =>
          createBusinessCaseSignal(
            'offerExpiringSoon',
            'warning',
            {offerValidTo, daysUntilExpiry},
            input.evaluatedAt
          )
        )
        .otherwise(() => null);
    });

const detectOfferExpired = (input: DetectBusinessCaseSignalsInput): BusinessCaseSignal | null =>
  match(getOfferValidTo(input))
    .returnType<BusinessCaseSignal | null>()
    .with(Pattern.nullish, () => null)
    .otherwise((offerValidTo) =>
      match({
        activeOffer: input.facts.state === 'OFFER',
        expired: isBefore(parseISO(offerValidTo), parseISO(input.evaluatedAt)),
      })
        .returnType<BusinessCaseSignal | null>()
        .with({activeOffer: true, expired: true}, () =>
          createBusinessCaseSignal(
            'offerExpired',
            'danger',
            {
              offerValidTo,
              expiredForDays: differenceInWholeDays(input.evaluatedAt, offerValidTo),
            },
            input.evaluatedAt
          )
        )
        .otherwise(() => null)
    );

const detectTestDriveCompletedNoFollowUp = (
  input: DetectBusinessCaseSignalsInput
): BusinessCaseSignal | null => {
  const completedEvent = input.facts.timeline.find((event) => event.kind === 'testDriveCompleted');

  return match(completedEvent)
    .returnType<BusinessCaseSignal | null>()
    .with(Pattern.nullish, () => null)
    .otherwise((event) => {
      const hasFollowUp = input.facts.timeline.some(
        (candidate) =>
          candidate.direction === 'outbound' &&
          isAfter(parseISO(candidate.occurredAt), parseISO(event.occurredAt))
      );
      const daysSinceTestDrive = differenceInWholeDays(input.evaluatedAt, event.occurredAt);

      return match({
        hasFollowUp,
        reachedMinimum: daysSinceTestDrive >= input.policy.testDriveFollowUpMinimumDays,
      })
        .returnType<BusinessCaseSignal | null>()
        .with({hasFollowUp: false, reachedMinimum: true}, () =>
          createBusinessCaseSignal(
            'testDriveCompletedNoFollowUp',
            'warning',
            {
              testDriveAt: event.occurredAt,
              daysSinceTestDrive,
              triggeringEventId: event.id,
            },
            input.evaluatedAt,
            getTimelineEvidenceOmissions(input)
          )
        )
        .otherwise(() => null);
    });
};

const detectTestDrivePlannedSoon = (
  input: DetectBusinessCaseSignalsInput
): BusinessCaseSignal | null => {
  const plannedTestDrive = input.facts.plannedActivities.find((activity) =>
    isUpcomingTestDrive(activity, input.evaluatedAt)
  );

  return match(plannedTestDrive)
    .returnType<BusinessCaseSignal | null>()
    .with(Pattern.nullish, () => null)
    .otherwise((activity) => {
      const hoursUntilTestDrive = differenceInWholeHours(activity.dueAt, input.evaluatedAt);

      return match(hoursUntilTestDrive <= input.policy.testDriveSoonHours)
        .returnType<BusinessCaseSignal | null>()
        .with(true, () =>
          createBusinessCaseSignal(
            'testDrivePlannedSoon',
            'info',
            {
              testDriveAt: activity.dueAt,
              hoursUntilTestDrive,
              triggeringEventId: activity.id,
            },
            input.evaluatedAt,
            getPlannedActivityEvidenceOmissions(input)
          )
        )
        .otherwise(() => null);
    });
};

const detectCloseBlocked = (input: DetectBusinessCaseSignalsInput): BusinessCaseSignal | null =>
  match({
    contract: input.facts.state === 'CONTRACT',
    incompleteChecklistItemCount: input.facts.incompleteChecklistItemCount,
  })
    .returnType<BusinessCaseSignal | null>()
    .with(
      {contract: true, incompleteChecklistItemCount: Pattern.number.gt(0)},
      ({incompleteChecklistItemCount}) =>
        createBusinessCaseSignal(
          'closeBlocked',
          'danger',
          {incompleteChecklistItemCount},
          input.evaluatedAt,
          input.facts.completeness.checklistComplete ? [] : ['checklistUnavailable']
        )
    )
    .otherwise(() => null);

const hasComparableBuyingPriceCurrencies = (input: DetectBusinessCaseSignalsInput) => {
  const customerCurrency = input.facts.purchaseVehicle?.customerExpectedPrice?.currency;
  const maximumCurrency = input.facts.purchaseVehicle?.maxPurchasePrice?.currency;

  return isNil(customerCurrency) || isNil(maximumCurrency) || customerCurrency === maximumCurrency;
};

const detectExpectedPurchaseDatePassed = (
  input: DetectBusinessCaseSignalsInput
): BusinessCaseSignal | null =>
  match(input.facts.purchaseVehicle?.expectedPurchaseOn ?? input.facts.expectedPurchaseDate)
    .returnType<BusinessCaseSignal | null>()
    .with(Pattern.nullish, () => null)
    .otherwise((expectedPurchaseDate) => {
      const expectedPurchaseAt = `${expectedPurchaseDate.slice(0, 10)}T00:00:00.000Z`;

      return match(differenceInWholeDays(input.evaluatedAt, expectedPurchaseAt) > 0)
        .returnType<BusinessCaseSignal | null>()
        .with(true, () =>
          createBusinessCaseSignal(
            'expectedPurchaseDatePassed',
            'warning',
            {
              expectedPurchaseDate,
              daysPastExpectedPurchaseDate: differenceInWholeDays(
                input.evaluatedAt,
                expectedPurchaseAt
              ),
            },
            input.evaluatedAt
          )
        )
        .otherwise(() => null);
    });

const detectExpectedPriceAboveMax = (
  input: DetectBusinessCaseSignalsInput
): BusinessCaseSignal | null =>
  match({
    customerRequestedPrice:
      input.facts.purchaseVehicle?.customerExpectedPrice?.amount ??
      input.facts.customerRequestedPrice,
    maximumBuyingPrice:
      input.facts.purchaseVehicle?.maxPurchasePrice?.amount ?? input.facts.maximumBuyingPrice,
    comparableCurrencies: hasComparableBuyingPriceCurrencies(input),
  })
    .returnType<BusinessCaseSignal | null>()
    .with(
      {
        customerRequestedPrice: Pattern.number,
        maximumBuyingPrice: Pattern.number,
        comparableCurrencies: true,
      },
      ({customerRequestedPrice, maximumBuyingPrice}) =>
        match(customerRequestedPrice > maximumBuyingPrice)
          .returnType<BusinessCaseSignal | null>()
          .with(true, () =>
            createBusinessCaseSignal(
              'expectedPriceAboveMax',
              'warning',
              {customerRequestedPrice, maximumBuyingPrice},
              input.evaluatedAt
            )
          )
          .otherwise(() => null)
    )
    .otherwise(() => null);

const getPaymentUnpaidForDays = (
  input: DetectBusinessCaseSignalsInput,
  payment: BusinessCasePaymentEvidence
): number | null =>
  match(payment)
    .with({dueOn: Pattern.string}, ({dueOn}) => {
      const overdueForDays = differenceInWholeDays(input.evaluatedAt, dueOn);

      return overdueForDays > 0 ? overdueForDays : null;
    })
    .with({issuedAt: Pattern.string}, ({issuedAt}) => {
      const unpaidHours = differenceInWholeHours(input.evaluatedAt, issuedAt);

      return unpaidHours >= input.policy.paymentUnpaidMinimumDays * HOURS_PER_DAY
        ? Math.floor(unpaidHours / HOURS_PER_DAY)
        : null;
    })
    .otherwise(() => null);

const detectUnpaid = (
  input: DetectBusinessCaseSignalsInput,
  code: UnpaidSignalCode,
  kind: BusinessCasePaymentKind
): BusinessCaseSignal | null => {
  const overduePayment = input.facts.payments
    .flatMap((payment) => {
      if (payment.kind !== kind || payment.state !== 'unpaid') {
        return [];
      }

      const unpaidForDays = getPaymentUnpaidForDays(input, payment);

      return isNil(unpaidForDays) ? [] : [{payment, unpaidForDays}];
    })
    .at(0);

  return match({
    overduePayment,
    paymentEvidenceComplete: input.facts.completeness.paymentEvidenceComplete,
  })
    .returnType<BusinessCaseSignal | null>()
    .with(
      {
        overduePayment: Pattern.nonNullable,
        paymentEvidenceComplete: true,
      },
      ({overduePayment: {payment, unpaidForDays}}) =>
        createUnpaidBusinessCaseSignal(
          code,
          {
            ...(isNotNil(payment.amount) ? {amount: payment.amount} : {}),
            ...(isNotNil(payment.currency) ? {currency: payment.currency} : {}),
            ...(isNotNil(payment.dueOn) ? {dueOn: payment.dueOn} : {}),
            ...(isNotNil(payment.issuedAt) ? {issuedAt: payment.issuedAt} : {}),
            unpaidForDays,
          },
          input.evaluatedAt
        )
    )
    .otherwise(() => null);
};

const detectSignal = (
  code: BusinessCaseSignalCode,
  input: DetectBusinessCaseSignalsInput,
  emittedSignalCodes: ReadonlySet<BusinessCaseSignalCode>
): BusinessCaseSignal | null =>
  match(code)
    .returnType<BusinessCaseSignal | null>()
    .with('customerAwaitingReply', () => detectCustomerAwaitingReply(input))
    .with('overdueTask', () => detectOverdueTask(input))
    .with('missingNextStep', () => detectMissingNextStep(input, emittedSignalCodes))
    .with('goingCold', () => detectGoingCold(input, emittedSignalCodes))
    .with('engagement', () => detectEngagement(input))
    .with('offerNotSent', () => detectOfferNotSent(input))
    .with('offerSentNoResponse', () => detectOfferSentNoResponse(input))
    .with('offerOpenedNoReply', () => detectOfferOpenedNoReply(input))
    .with('offerExpiringSoon', () => detectOfferExpiringSoon(input))
    .with('offerExpired', () => detectOfferExpired(input))
    .with('testDriveCompletedNoFollowUp', () => detectTestDriveCompletedNoFollowUp(input))
    .with('testDrivePlannedSoon', () => detectTestDrivePlannedSoon(input))
    .with('depositUnpaid', () => detectUnpaid(input, 'depositUnpaid', 'deposit'))
    .with('invoiceUnpaid', () => detectUnpaid(input, 'invoiceUnpaid', 'invoice'))
    .with('closeBlocked', () => detectCloseBlocked(input))
    .with('expectedPurchaseDatePassed', () => detectExpectedPurchaseDatePassed(input))
    .with('expectedPriceAboveMax', () => detectExpectedPriceAboveMax(input))
    .exhaustive();

const isSignalAllowedForState = (
  code: BusinessCaseSignalCode,
  input: DetectBusinessCaseSignalsInput
) =>
  match({code, state: input.facts.state})
    .with({code: 'depositUnpaid'}, {code: 'invoiceUnpaid'}, () => true)
    .with({state: 'PAID'}, () => false)
    .otherwise(() => true);

export const detectBusinessCaseSignals = (
  input: DetectBusinessCaseSignalsInput
): BusinessCaseSignal[] => {
  const enabledSignalCodes = new Set(input.policy.enabledSignalCodes);
  const signals: BusinessCaseSignal[] = [];
  const emittedSignalCodes = new Set<BusinessCaseSignalCode>();

  BUSINESS_CASE_SIGNAL_CODES.filter(
    (code) =>
      enabledSignalCodes.has(code) &&
      isSignalApplicable(code, input) &&
      isSignalAllowedForState(code, input)
  ).forEach((code) => {
    match(detectSignal(code, input, emittedSignalCodes))
      .with(Pattern.nonNullable, (signal) => {
        signals.push(signal);
        emittedSignalCodes.add(signal.code);
      })
      .otherwise(() => undefined);
  });

  return signals;
};
